import { RequestTheme } from 'typings/shared'
import { normalizeTrackingLabel } from '@utils/trackingNaming'

const buildNormalizedFilterGroup = (
	groupName: string,
	frenchLabel: string | undefined
): string => {
	const normalizedLabel = frenchLabel ? normalizeTrackingLabel(frenchLabel) : ''
	return normalizedLabel ? `${groupName}:[${normalizedLabel}]` : ''
}

export const buildCategorySearchFiltersPayload = (
	selectedCategoryNames: string[],
	currentRequestThemes: RequestTheme[] = [],
	frenchRequestThemes: RequestTheme[] = []
): string => {
	const currentCategoriesByName = new Map(
		currentRequestThemes.flatMap((theme) =>
			theme.requestSubThemes.map((subTheme) => [subTheme.name, subTheme])
		)
	)
	const frenchCategoryTitles = new Map<string, string>()

	frenchRequestThemes.forEach((theme) => {
		theme.requestSubThemes.forEach((subTheme) => {
			frenchCategoryTitles.set(`id:${subTheme.id}`, subTheme.title)
			frenchCategoryTitles.set(`name:${subTheme.name}`, subTheme.title)
		})
	})

	const normalizedCategories = selectedCategoryNames
		.map((categoryName) => {
			const currentCategory = currentCategoriesByName.get(categoryName)
			const frenchTitleById = currentCategory
				? frenchCategoryTitles.get(`id:${currentCategory.id}`)
				: undefined

			return (
				frenchTitleById ||
				frenchCategoryTitles.get(`name:${categoryName}`) ||
				''
			)
		})
		.map(normalizeTrackingLabel)
		.filter(Boolean)
		.sort((categoryA, categoryB) => categoryA.localeCompare(categoryB))

	return `category:[${normalizedCategories.join(',')}]`
}

export const buildRequestTypeSearchFiltersPayload = (
	selectedRequestTypeNames: string[],
	currentRequestThemes: RequestTheme[] = [],
	frenchRequestThemes: RequestTheme[] = []
): string => {
	const currentThemesByName = new Map(
		currentRequestThemes.map((theme) => [theme.name, theme])
	)
	const frenchThemeTitles = new Map<string, string>()

	frenchRequestThemes.forEach((theme) => {
		frenchThemeTitles.set(`id:${theme.id}`, theme.title)
		frenchThemeTitles.set(`name:${theme.name}`, theme.title)
	})

	const normalizedTypes = selectedRequestTypeNames
		.map((requestTypeName) => {
			const currentTheme = currentThemesByName.get(requestTypeName)
			return (
				(currentTheme
					? frenchThemeTitles.get(`id:${currentTheme.id}`)
					: undefined) || frenchThemeTitles.get(`name:${requestTypeName}`) || ''
			)
		})
		.map(normalizeTrackingLabel)
		.filter(Boolean)
		.sort((typeA, typeB) => typeA.localeCompare(typeB))

	return `type:[${normalizedTypes.join(',')}]`
}

export const buildRequestSearchFiltersPayload = (
	selectedCategoryNames: string[],
	selectedRequestTypeNames: string[],
	currentRequestThemes: RequestTheme[] = [],
	frenchRequestThemes: RequestTheme[] = []
): string =>
	[
		selectedCategoryNames.length
			? buildCategorySearchFiltersPayload(
					selectedCategoryNames,
					currentRequestThemes,
					frenchRequestThemes
				)
			: '',
		selectedRequestTypeNames.length
			? buildRequestTypeSearchFiltersPayload(
					selectedRequestTypeNames,
					currentRequestThemes,
					frenchRequestThemes
				)
			: ''
	]
		.filter(Boolean)
		.join(',')

const REQUEST_STATUS_FRENCH_ASSET_BY_VALUE: Record<number, string> = {
	0: 'page_myRequest_select_allStatuses',
	1: 'page_myRequest_select_open',
	2: 'page_myRequest_select_processing',
	3: 'page_myRequest_select_payment',
	4: 'page_myRequest_select_awaiting_file'
}

const REQUEST_TAB_FRENCH_ASSET_BY_VALUE: Record<string, string> = {
	active: 'page_myRequest_tabFilter_active',
	closed: 'page_myRequest_tabFilter_closed'
}

const REQUEST_SORT_FRENCH_ASSET_BY_VALUE: Record<string, string> = {
	dateAdd_desc: 'page_myRequest_select_creationDate_desc',
	dateAdd_asc: 'page_myRequest_select_creationDate_asc',
	dateUpdate_desc: 'page_myRequest_select_lastModificationDate_desc',
	dateUpdate_asc: 'page_myRequest_select_lastModificationDate_asc'
}

export const buildMyRequestsSearchFiltersPayload = (
	tabFilter: string,
	statusFilter: number,
	sortFilter: string,
	frenchAssets: Record<string, string> = {}
): string =>
	[
		buildNormalizedFilterGroup(
			'tab',
			frenchAssets[REQUEST_TAB_FRENCH_ASSET_BY_VALUE[tabFilter]]
		),
		buildNormalizedFilterGroup(
			'status',
			frenchAssets[REQUEST_STATUS_FRENCH_ASSET_BY_VALUE[statusFilter]]
		),
		buildNormalizedFilterGroup(
			'sort',
			frenchAssets[REQUEST_SORT_FRENCH_ASSET_BY_VALUE[sortFilter]]
		)
	]
		.filter(Boolean)
		.join(',')